import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { FileSystem } from '~/eds/src/components/FileSystem/FileSystem';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusText } from '~/eds/src/components/StatusText/StatusText';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { ResolvedCitationEntity } from '../../../types/chat';

interface Props {
  isLoading?: boolean;
  text: string;
  resolvedCitation?: ResolvedCitationEntity;
  error?: Error;
}

export const CitationDetails = ({
  error,
  isLoading,
  text,
  resolvedCitation,
}: Props) => {
  const loadingContent = { isLoading, shimmer: { height: '30px' } };
  const formatDate = useFormatDate();
  const intl = useIntl();
  return (
    <Layout spacing={3} direction="column">
      <ContentContainer loadingContent={loadingContent}>
        {resolvedCitation?.fileName ? (
          <Filename
            name={resolvedCitation?.fileName ?? ''}
            link={{ pathname: resolvedCitation?.link }}
          />
        ) : undefined}
      </ContentContainer>

      <Box
        borderRadius="m"
        p={3}
        bg="status.active.light"
        border="active.light"
      >
        {text}
      </Box>
      <ContentContainer loadingContent={loadingContent}>
        {resolvedCitation || error ? (
          <Layout direction="column" spacing={1} color="text.secondary">
            {
              // If the error is not found, show a message that the file has been deleted
              error?.cause === HttpStatusCodeType.NotFound ? (
                <StatusText
                  text={error.message}
                  icon="status-warning"
                  iconColor="status.warning"
                  textColor="text.secondary"
                  iconSpacing="s"
                />
              ) : (
                <>
                  {resolvedCitation?.path ? (
                    <FileSystem
                      folder={{
                        path: resolvedCitation?.path,
                      }}
                    />
                  ) : null}
                  {resolvedCitation?.lastModified ? (
                    <StatusText
                      text={intl.formatMessage(
                        {
                          id: 'CitationDetails.lastModified',
                          defaultMessage: 'Last modified {date}',
                          description:
                            'Shows when the document was last modified. Example: date = "December 15, 2023"',
                        },
                        {
                          date: formatDate(
                            resolvedCitation?.lastModified ?? null,
                            'full',
                          ),
                        },
                      )}
                      icon="field-date"
                      iconColor="status.info"
                      textColor="text.secondary"
                      iconSpacing="s"
                    />
                  ) : null}
                </>
              )
            }
          </Layout>
        ) : (
          <></>
        )}
      </ContentContainer>
    </Layout>
  );
};
